import React, { useCallback, useEffect, useMemo, useState } from "react";

import AccountRelationshipsTab from "./account-relationships/AccountRelationshipsTab";
import { buildDefaultVendorContext } from "./account-relationships/relationshipDataAdapter";
import RelationshipAccountsLoader from "./RelationshipAccountsLoader";
import { useRelationshipAccountsFetch } from "./useRelationshipAccountsFetch";
import { useToast } from "../../Components/Toast";
import { useAppDispatch } from "../../Store/Hooks";
import {
  invalidateRelationshipMatrixCache,
  updateRelationshipAccount,
} from "../../Store/Reducers/RelationshipSlice";
import {
  getFromLocalStorage,
  STORAGE_KEYS,
} from "../../Utils/Helper/LocalStorage";

interface AccountRelationshipsShellProps {
  preferredAccountId?: string | null;
  focusVendorId?: string | null;
  focusContactId?: string | null;
  onFocusConsumed?: () => void;
}

const AccountRelationshipsShellInner: React.FC<
  AccountRelationshipsShellProps
> = ({
  preferredAccountId = null,
  focusVendorId = null,
  focusContactId = null,
  onFocusConsumed,
}) => {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const { accounts, accountsUserIdsKey, error, isScopeLoading } =
    useRelationshipAccountsFetch();

  const preferredId = preferredAccountId ? String(preferredAccountId) : null;

  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    preferredId
  );
  const [userPickedAccount, setUserPickedAccount] = useState(false);

  // New deep-link / scope change → allow preferred selection again.
  useEffect(() => {
    setUserPickedAccount(false);
    setSelectedAccountId(preferredId);
  }, [accountsUserIdsKey, preferredId]);

  useEffect(() => {
    if (!accounts.length) return;

    if (preferredId && !userPickedAccount) {
      const match = accounts.find(
        account => String(account.id) === preferredId
      );
      if (match) {
        setSelectedAccountId(String(match.id));
        dispatch(invalidateRelationshipMatrixCache(String(match.id)));
      }
      // Do not fall back to accounts[0] while a preferred id is pending.
      return;
    }

    setSelectedAccountId(current => {
      if (current && accounts.some(a => String(a.id) === String(current))) {
        return String(current);
      }
      return accounts[0]?.id ? String(accounts[0].id) : null;
    });
  }, [accounts, preferredId, userPickedAccount, dispatch]);

  const vendorContext = useMemo(() => buildDefaultVendorContext(), []);

  const executiveId = useMemo(() => {
    return loggedInUser?.executive_id || loggedInUser?.user_id || null;
  }, [loggedInUser?.executive_id, loggedInUser?.user_id]);

  const handleAccountRefreshed = useCallback(
    (refreshedAccount: any) => {
      dispatch(
        updateRelationshipAccount({
          id: refreshedAccount.id,
          ...refreshedAccount,
        })
      );
    },
    [dispatch]
  );

  const handlePairAssessed = useCallback(() => {
    toast.success("Relationship assessment saved.");
  }, [toast]);

  const handleSelectAccount = useCallback(
    (accountId: string) => {
      setUserPickedAccount(true);
      setSelectedAccountId(String(accountId));
      dispatch(invalidateRelationshipMatrixCache(String(accountId)));
    },
    [dispatch]
  );

  return (
    <div className="rl-prototype-skin">
      {isScopeLoading ? (
        <RelationshipAccountsLoader label="Loading account relationships…" />
      ) : error && !accounts.length ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
          {error}
        </div>
      ) : (
        <AccountRelationshipsTab
          accounts={accounts}
          selectedAccountId={selectedAccountId}
          onSelectAccount={handleSelectAccount}
          executiveId={executiveId}
          onPairAssessed={handlePairAssessed}
          vendorContext={vendorContext}
          onAccountRefreshed={handleAccountRefreshed}
          focusVendorId={focusVendorId}
          focusContactId={focusContactId}
          onFocusConsumed={onFocusConsumed}
        />
      )}
    </div>
  );
};

const AccountRelationshipsShell: React.FC<
  AccountRelationshipsShellProps
> = props => <AccountRelationshipsShellInner {...props} />;

export default AccountRelationshipsShell;
